import { useCallback, useEffect, useRef, useState } from 'react';

import { Toaster } from '@picsart/design-system/components/Toaster';
import {
  Snackbar,
  type SnackbarSkins,
} from '@picsart/design-system/components/Snackbar';
import { Notification } from '@picsart/design-system/components/Notification';
import { Button } from '@picsart/design-system/components/Button';
import { useTranslator } from '@picsart/rc/services/localization';
import { useDeviceType } from '@picsart/design-system/hooks/device';
import { useAppStore, setAppStore, type IAppStore } from '@/store';
import { ToastType } from '@/store/toast/types';
import { RightPanelTab } from '@/store/navigation/types';
import { isPortfolioApp as checkIsPortfolioApp } from '@/utils/environment/surface';
import { pushBrowserNav } from '@/hooks/useBrowserHistory';
import { Screen } from '@/types';

// How long the "creations ready" toast stays before auto-hiding.
const CREATIONS_TOAST_MS = 10000;
// Portfolio profile's "Assets" tab — where creations live (renders <Generations/>).
const PROFILE_ASSETS_TAB = 0;

const selector = (store: IAppStore) => ({
  toast: store.toast,
  hideToast: store.hideToast,
  pendingReadyCount: store.pendingReadyCount,
  resetPendingReadyCount: store.resetPendingReadyCount,
  setScreen: store.setScreen,
  setRightPanelTab: store.setRightPanelTab,
});

const toastTypeToSnackbarSkin = (type: ToastType): SnackbarSkins => {
  switch (type) {
    case ToastType.Error:
      return 'error';
    case ToastType.Info:
      return 'information';
    case ToastType.Success:
    default:
      return 'success';
  }
};

/**
 * Bottom-center toast surface. A finished-generation toast ("N creations ready
 * → View") takes precedence over the global info/error toast; tapping View
 * takes the user to the Creations grid. Generations are never auto-opened.
 */
const ToastSurface = () => {
  const translate = useTranslator();
  const { isMobile } = useDeviceType();
  const {
    toast: globalToast,
    hideToast: hideGlobalToast,
    pendingReadyCount,
    resetPendingReadyCount,
    setScreen,
    setRightPanelTab,
  } = useAppStore(selector);

  const [isReadyToastOpen, setIsReadyToastOpen] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Show / refresh the toast whenever another generation finishes.
  useEffect(() => {
    if (pendingReadyCount <= 0) {
      setIsReadyToastOpen(false);
      return undefined;
    }
    setIsReadyToastOpen(true);
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(
      () => setIsReadyToastOpen(false),
      CREATIONS_TOAST_MS
    );
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [pendingReadyCount]);

  const handleViewCreations = useCallback(() => {
    setIsReadyToastOpen(false);
    if (isMobile && checkIsPortfolioApp()) {
      // Portfolio: creations live in the profile's Assets tab, not the Files
      // screen. Open the profile there (that tab renders <Generations/>).
      setScreen(Screen.Library);
      setAppStore({ isProfileOpen: true, profileTab: PROFILE_ASSETS_TAB });
      pushBrowserNav('profile');
    } else {
      setScreen(Screen.Files);
      if (!isMobile) {
        setRightPanelTab(RightPanelTab.Creations);
      }
    }
    resetPendingReadyCount();
  }, [isMobile, setScreen, setRightPanelTab, resetPendingReadyCount]);

  const readyMessage =
    pendingReadyCount === 1
      ? 'Your creation is ready!'
      : `${pendingReadyCount} creations ready`;

  const showReadyToast = isReadyToastOpen && pendingReadyCount > 0;

  return (
    <Toaster
      dataTestId="toast-surface"
      isOpen={showReadyToast || Boolean(globalToast)}
      position="bottomLeft"
      onClose={
        showReadyToast ? () => setIsReadyToastOpen(false) : hideGlobalToast
      }
      isWithoutTiming
    >
      {showReadyToast ? (
        <Notification
          dataTestId="creations-ready-toast"
          status="success"
          size="small"
          description={translate(readyMessage)}
          onClose={() => setIsReadyToastOpen(false)}
          actions={[
            <Button
              key="view"
              dataTestId="creations-ready-view"
              skin="primary"
              variant="text"
              size="small"
              onClick={handleViewCreations}
            >
              {translate('View')}
            </Button>,
          ]}
        />
      ) : globalToast ? (
        <Snackbar
          dataTestId="toast"
          skin={toastTypeToSnackbarSkin(globalToast.type)}
          title={translate(globalToast.message)}
          handleClose={hideGlobalToast}
        />
      ) : null}
    </Toaster>
  );
};

export default ToastSurface;
